import { environment } from '@env/environment';

export interface DebugFeature {
    id: string;
    name: string;
    description: string;
    icon: string;
    enabled: boolean;
    type: 'toggle' | 'action';
    category: string;
}

export const DEFAULT_DEBUG_FEATURES: DebugFeature[] = [
    {
        id: 'environment',
        name: 'Umgebung',
        description: 'Zwischen Produktions- und Testumgebungen wechseln',
        icon: 'tune',
        enabled: !environment.production,
        type: 'toggle',
        category: 'System',
    },
    {
        id: 'logging',
        name: 'Ausführliche Protokollierung',
        description: 'Detaillierte Konsolenprotokollierung aktivieren',
        icon: 'receipt_long',
        enabled: false,
        type: 'toggle',
        category: 'System',
    },
    {
        id: 'clearCache',
        name: 'Cache leeren',
        description: 'Browser-Cache leeren und neu laden',
        type: 'action',
        icon: 'clear_all',
        enabled: false,
        category: 'Wartung',
    },
    {
        id: 'clearStorage',
        name: 'Speicher leeren',
        description: 'LocalStorage und SessionStorage leeren',
        icon: 'delete_sweep',
        enabled: false,
        type: 'action',
        category: 'Wartung',
    },
    {
        id: 'reloadApp',
        name: 'Anwendung neu laden',
        description: 'Die Anwendung aktualisieren',
        icon: 'refresh',
        enabled: false,
        type: 'action',
        category: 'System',
    },
    {
        id: 'createBundle',
        name: 'Bundle erstellen',
        description: 'Erstellen Sie einen neuen Bundle-Code',
        icon: 'code',
        enabled: false,
        type: 'action',
        category: 'Warenausgang',
    },
    {
        id: 'compareArticle',
        name: 'Artikel vergleichen',
        description:
            'Hochladen von Artikeldaten zum automatisierten Vergleichen',
        icon: 'inventory_2',
        enabled: false,
        type: 'action',
        category: 'Artikelverwaltung',
    },
    {
        id: 'updateAsn',
        name: 'ASN hochladen',
        description: 'Hochladen von ASN Daten',
        icon: 'inventory_2',
        enabled: false,
        type: 'action',
        category: 'Artikelverwaltung',
    },
    {
        id: 'manageArticles',
        name: 'Artikel verwalten',
        description: 'Verwalten Sie Artikel in einer nicht ausgecheckten Mappe',
        icon: 'edit',
        enabled: false,
        type: 'action',
        category: 'Artikelverwaltung',
    },
    {
        id: 'createSupplier',
        name: 'Lieferant erstellen',
        description: 'Erstellen Sie einen neuen Lieferanten',
        icon: 'local_shipping',
        enabled: false,
        type: 'action',
        category: 'Lieferantenverwaltung',
    },
];

/**
 * Utility function to get a feature by ID
 */
export function getFeatureById(
    features: DebugFeature[],
    id: string
): DebugFeature | undefined {
    return features.find(feature => feature.id === id);
}

/**
 * Register a new feature
 */
export function registerFeature(
    features: DebugFeature[],
    newFeature: DebugFeature
): DebugFeature[] {
    const existingIndex = features.findIndex(
        feature => feature.id === newFeature.id
    );

    if (existingIndex !== -1) {
        const updatedFeatures = [...features];
        updatedFeatures[existingIndex] = {
            ...features[existingIndex],
            ...newFeature,
        };
        return updatedFeatures;
    } else {
        return [...features, newFeature];
    }
}

/**
 * Categories for organizing features in the UI
 */
export interface FeatureCategory {
    name: string;
    features: DebugFeature[];
}

/**
 * Group features by category (toggles and actions)
 */
export function categorizeFeatures(
    features: DebugFeature[]
): FeatureCategory[] {
    const toggleFeatures = features.filter(
        f => f.type === 'toggle' && f.id !== 'environment'
    );

    const actionFeatures = features.filter(f => f.type === 'action');

    return [
        { name: 'Settings', features: toggleFeatures },
        { name: 'Actions', features: actionFeatures },
    ];
}
